import React from "react";
import { Container, Box, Typography, Divider, Link } from "@mui/material";

export default function PrivacyPolicyPage() {
  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} gutterBottom>
          Privacy Policy
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Last updated: February 3, 2026
        </Typography>
      </Box>

      <Section title="1. Information We Collect">
        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
          ResourceNAV ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we
          collect, use, and safeguard information when you use the ResourceNAV platform (the "Platform").
        </Typography>
        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
          We collect information to operate and improve the Platform, including:
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>Account data: name, email address, password hash, role.</li>
          <li>Usage data: IP address, device and browser information, pages viewed, timestamps.</li>
          <li>Authentication and security data: session cookies, CSRF tokens.</li>
          <li>Analytics data: Google Analytics events (IP anonymized).</li>
          <li>Email data: transactional emails sent via Postmark.</li>
        </Box>
        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
          We also collect company and public data, including company details you submit and publicly available
          information used to populate company profiles.
        </Typography>
      </Section>

      <Section title="2. Legal Basis for Processing (EEA)">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          If you are located in the European Economic Area (EEA), our legal bases for processing personal data include:
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>Contractual necessity: account creation, login, and providing Platform features.</li>
          <li>Legitimate interests: security, fraud prevention, and platform integrity.</li>
          <li>Consent: analytics cookies and optional measurements in the EU/EEA.</li>
        </Box>
      </Section>

      <Section title="3. How We Use Information">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          We use information to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>Provide and maintain the Platform.</li>
          <li>Verify company claims and profile updates.</li>
          <li>Improve data accuracy and platform features.</li>
          <li>Communicate with users about updates, security, and support.</li>
          <li>Generate aggregated insights and analytics (non-identifiable).</li>
          <li>Comply with legal obligations.</li>
        </Box>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          We do not sell personal data.
        </Typography>
      </Section>

      <Section title="4. Cookies & Analytics (Cookie Policy)">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          We use cookies and similar technologies for authentication, security, and analytics.
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>Essential cookies: session cookies and CSRF tokens required for login and security.</li>
          <li>Optional analytics cookies: Google Analytics cookies used to understand platform usage.</li>
        </Box>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          Common analytics cookies include:
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>`_ga`: used to distinguish users, typically retained up to 2 years.</li>
          <li>`_gid`: used to distinguish users, typically retained up to 24 hours.</li>
          <li>`_ga_*`: used to persist session state, typically retained up to 2 years.</li>
        </Box>
        <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 1 }}>
          Essential cookies do not require consent. If you are in the EU/EEA, we only load analytics cookies after you
          give explicit consent. You can withdraw consent at any time by rejecting analytics in the cookie banner (or
          clearing your browser storage and reloading the page).
        </Typography>
      </Section>

      <Section title="5. Sharing of Information & Third-Party Processors">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          We may share information with service providers who help operate the Platform, including:
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>Google Analytics (Google LLC).</li>
          <li>Postmark (ActiveCampaign) for transactional email delivery.</li>
          <li>Hosting providers such as Heroku and Vercel.</li>
        </Box>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          We may also disclose information to comply with legal requirements or protect platform integrity.
        </Typography>
      </Section>

      <Section title="6. International Data Transfers">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          We are based in the United States and may transfer personal data to the United States and other countries
          where our service providers operate. When required, we rely on appropriate safeguards for cross-border
          transfers, such as Standard Contractual Clauses (SCCs). For details on SCCs as implemented by Google, see{" "}
          <Link href="https://policies.google.com/privacy/frameworks?hl=en-US">
            Google&apos;s data transfer frameworks
          </Link>{" "}
          and{" "}
          <Link href="https://business.safety.google/adsdatatransfers/">
            Google&apos;s information about international data transfers for analytics
          </Link>
          . For Postmark, our Data Processing Addendum incorporates SCCs as part of GDPR compliance. See{" "}
          <Link href="https://postmarkapp.com/dpa">Postmark&apos;s Data Processing Addendum</Link> and{" "}
          <Link href="https://postmarkapp.com/support/article/1218-gdpr-faq">Postmark&apos;s GDPR FAQ</Link>.
        </Typography>
      </Section>

      <Section title="7. EU Establishment">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          We do not have an establishment in the EU. We process personal data only occasionally and at low risk.
        </Typography>
      </Section>

      <Section title="8. Company Profiles & Public Visibility">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          Company profiles may contain public and user-submitted information. Claimed company profiles allow authorized
          representatives to submit updates and context. Approved updates may be visible to all platform users.
        </Typography>
      </Section>

      <Section title="9. Data Security">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          We take reasonable administrative, technical, and organizational measures to protect information. However, no
          system is completely secure, and we cannot guarantee absolute security.
        </Typography>
      </Section>

      <Section title="10. Data Retention">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          We retain information as follows:
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>Account data: while your account remains active.</li>
          <li>Security and access logs: up to 30 days.</li>
          <li>Analytics data: up to 14 months (Google Analytics default retention).</li>
        </Box>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          When you delete your account, we delete or de-identify personal data associated with your account where
          feasible, subject to legal obligations and our retention policies.
        </Typography>
      </Section>

      <Section title="11. Data Deletion Scope">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          When you delete your account, we remove your account profile and associated personal data from active systems.
          We may retain limited data in backups, security logs, and audit records until those records are automatically
          overwritten or expire under our retention schedule, or where retention is required by law.
        </Typography>
      </Section>

      <Section title="12. Your Rights (EU/EEA)">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          Depending on your location, you may have the right to:
        </Typography>
        <Box component="ul" sx={{ pl: 3, lineHeight: 1.7, mb: 1 }}>
          <li>Access your personal data.</li>
          <li>Rectify inaccurate data.</li>
          <li>Request deletion of your account and associated data.</li>
          <li>Request data portability.</li>
          <li>Withdraw consent for analytics cookies.</li>
          <li>Lodge a complaint with your local EU/EEA Data Protection Authority.</li>
        </Box>
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          You can contact us to exercise these rights.
        </Typography>
      </Section>

      <Section title="13. Children’s Privacy">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          The Platform is not intended for individuals under 18. We do not knowingly collect data from children.
        </Typography>
      </Section>

      <Section title="14. Changes to This Policy">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated
          effective date.
        </Typography>
      </Section>

      <Section title="15. Contact Us (Data Controller)">
        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
          The data controller for ResourceNAV is SOPHIACI LLC, 3800 AMERICAN BLVD. WEST, SUITE 1500 #300-030
BLOOMINGTON, MN 55431. If you have questions about
          this Privacy Policy or our data practices, contact us at{" "}
          <Link href="mailto:resourcenav@naturalresourcestocks.net">
            resourcenav@naturalresourcestocks.net
          </Link>
          .
        </Typography>
      </Section>
    </Container>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        {title}
      </Typography>
      {children}
      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}
